import {
    getNutrientWebviewLoadOptions,
    isPSPDFKitAnnotationImportError,
    loadNutrientViewer,
    prepareInstantJSONForImport,
} from "utilities/nutrient/nutrient.utility";

export interface IFlattenPlanAnnotationsOptions {
    licenseKey: string | undefined;
    documentUrl: string;
    /** InstantJSON payload as a JSON string. Used when xfdf is not provided. */
    instantJSON?: string;
    /** XFDF payload. Preferred over instantJSON when both are present. */
    xfdf?: string;
    /** Aborts the load + export if it exceeds this many ms. Defaults to 60 seconds. */
    timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 60_000;

function getBaseUrl(): string {
    return `${window.location.protocol}//${window.location.host}${import.meta.env.BASE_URL}`;
}

/**
 * Loads a plan-page PDF + its annotation payload in Nutrient's headless mode,
 * exports a flattened copy as a Blob, then disposes the instance. Used by the
 * version-history download flow so we can produce an annotated PDF for any
 * version (including ones not currently displayed) without disturbing the
 * user's active viewer. Headless mode skips the UI entirely — no DOM container
 * is needed, which is faster than mounting and tearing down toolbars/sidebars
 * that we'd never see.
 *
 * Nutrient's .load() still briefly mutates document.body (prepends an
 * aria-live region for screen-reader announcements), which would normally
 * make the page scrollbar flicker. PlanPageDetails suppresses that by setting
 * `overflow: hidden` on document.documentElement while the viewer is
 * mounted, so the flatten doesn't cause a layout shift.
 */
export async function flattenPlanAnnotations({
    licenseKey,
    documentUrl,
    instantJSON,
    xfdf,
    timeoutMs = DEFAULT_TIMEOUT_MS,
}: IFlattenPlanAnnotationsOptions): Promise<Blob> {
    const NutrientViewer = await loadNutrientViewer();

    let instance: Awaited<ReturnType<typeof NutrientViewer.load>> | null = null;
    let timeoutHandle: number | null = null;

    try {
        // The @nutrient-sdk/viewer TS types model `container` as required on
        // StandaloneConfiguration and don't have a dedicated headless variant
        // that drops it. The runtime accepts (and the Nutrient docs require)
        // omitting `container` when `headless: true` — passing it is
        // explicitly called out as not making sense. Build the config as a
        // permissive shape and assert to the SDK's expected type to bridge
        // that gap.
        const config = {
            licenseKey,
            headless: true,
            document: documentUrl,
            baseUrl: getBaseUrl(),
            // The plan viewer this download runs alongside primes the WASM worker
            // with the webview memory options; a load that omits them trips
            // Nutrient's config-mismatch assertion and the download fails.
            ...getNutrientWebviewLoadOptions(),
            autoSaveMode: NutrientViewer.AutoSaveMode.DISABLED,
        } as Parameters<typeof NutrientViewer.load>[0] & {
            XFDF?: string;
            instantJSON?: unknown;
        };

        // Annotation payload: prefer XFDF when available (modern path), fall
        // back to InstantJSON (legacy rows). Mirrors NutrientPlanViewer.tsx.
        if (xfdf) {
            config.XFDF = xfdf;
        } else if (instantJSON) {
            // Drops pdfId *and* skippedPdfObjectIds together — see
            // prepareInstantJSONForImport. Keeping the object-id references while
            // discarding the identity they belong to is what made stale payloads
            // fail deep in the WASM core (ado-283518).
            config.instantJSON = prepareInstantJSONForImport(
                JSON.parse(instantJSON) as Record<string, unknown>,
            );
        }

        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutHandle = window.setTimeout(() => {
                reject(new Error(`flattenPlanAnnotations timed out after ${timeoutMs}ms`));
            }, timeoutMs);
        });

        try {
            instance = await Promise.race([NutrientViewer.load(config), timeoutPromise]);
        } catch (error) {
            // Deliberately NOT retrying without annotations here, unlike the
            // viewer. The viewer falls back because a readable plan beats no plan,
            // and it tells the user the markups are missing. A download has no such
            // channel: handing back a "<name>-markups.pdf" that silently contains no
            // markups is worse than failing — the user would file it, print it, or
            // send it to a sub believing the markups are on it. Fail loudly and let
            // downloadPlanPageAnnotated surface its error message instead.
            if (isPSPDFKitAnnotationImportError(error)) {
                throw new Error(
                    "Plan annotations could not be applied to the document, so an annotated copy can't be produced.",
                    { cause: error },
                );
            }
            throw error;
        }

        const buffer = await Promise.race([instance.exportPDF({ flatten: true }), timeoutPromise]);

        return new Blob([buffer], { type: "application/pdf" });
    } finally {
        if (timeoutHandle !== null) {
            window.clearTimeout(timeoutHandle);
        }
        if (instance) {
            try {
                NutrientViewer.unload(instance);
            } catch {
                // Best effort — if Nutrient throws on unload (already unloaded,
                // etc.) there's nothing else to clean up since headless mode
                // doesn't own any DOM nodes.
            }
        }
    }
}
